import { useRef, useEffect, useMemo, useState, useCallback } from "react";
import type { WppChat, WppMessage, ReplyContext, LocationData } from "@/hooks/useWhatsAppChat";
import MessageBubble, { GroupPosition } from "./MessageBubble";
import ChatInput from "./ChatInput";
import ReplyPreview from "./ReplyPreview";
import ChatWindowShimmer from "./ChatWindowShimmer";
import { useTranslation } from "react-i18next";

interface ChatWindowProps {
  chat: WppChat | null;
  messages: WppMessage[];
  loading: boolean;
  sendingMessage: boolean;
  error: string | null;
  onSendMessage: (phone: string, text: string, isGroup?: boolean) => Promise<boolean>;
  onSendReply: (phone: string, messageId: string, text: string, isGroup?: boolean) => Promise<boolean>;
  onSendImage: (phone: string, base64: string, filename: string, caption?: string, isGroup?: boolean) => Promise<boolean>;
  onSendFile: (phone: string, base64: string, filename: string, caption?: string, isGroup?: boolean) => Promise<boolean>;
  onSendVoice: (phone: string, base64Ptt: string, isGroup?: boolean) => Promise<boolean>;
  onSendLocation: (phone: string, location: LocationData, isGroup?: boolean) => Promise<boolean>;
  onSendLink: (phone: string, url: string, caption?: string, isGroup?: boolean) => Promise<boolean>;
  onReactMessage: (messageId: string, reaction: string) => Promise<boolean>;
  onRetryMessages: (phone: string) => void;
  onDownloadMedia: (messageId: string) => Promise<string | null>;
  onAiReply?: (message: WppMessage) => Promise<boolean>;
  aiReplying?: boolean;
  onBack?: () => void;
}

function getChatName(chat: WppChat): string {
  return (
    chat.contact?.name ||
    chat.contact?.pushname ||
    chat.contact?.formattedName ||
    chat.contact?.shortName ||
    chat.groupMetadata?.subject ||
    chat.id.user ||
    "Unknown"
  );
}

function formatDateLabel(timestamp: number, t: (k: string) => string): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) return t("chatWindow.today");
  if (diffDays === 1) return t("chatWindow.yesterday");
  return date.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function ChatWindow({
  chat,
  messages,
  loading,
  sendingMessage,
  error,
  onSendMessage,
  onSendReply,
  onSendImage,
  onSendFile,
  onSendVoice,
  onSendLocation,
  onSendLink,
  onReactMessage,
  onRetryMessages,
  onDownloadMedia,
  onAiReply,
  aiReplying,
  onBack,
}: ChatWindowProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [reply, setReply] = useState<ReplyContext | null>(null);

  const visibleMessages = useMemo(() => {
    return messages.filter(
      (msg) => msg.type !== "biz_content_placeholder" && msg.type !== "unknown"
    );
  }, [messages]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [visibleMessages]);

  const messageGroupPositions = useMemo((): GroupPosition[] => {
    const positions: GroupPosition[] = [];

    for (let i = 0; i < visibleMessages.length; i++) {
      const prev = i > 0 ? visibleMessages[i - 1] : null;
      const next = i < visibleMessages.length - 1 ? visibleMessages[i + 1] : null;

      const sameAsPrev = prev ? prev.fromMe === visibleMessages[i].fromMe : false;
      const sameAsNext = next ? next.fromMe === visibleMessages[i].fromMe : false;

      if (!sameAsPrev && !sameAsNext) {
        positions.push("single");
      } else if (!sameAsPrev && sameAsNext) {
        positions.push("first");
      } else if (sameAsPrev && sameAsNext) {
        positions.push("middle");
      } else {
        positions.push("last");
      }
    }

    return positions;
  }, [visibleMessages]);

  const handleReply = useCallback((ctx: ReplyContext) => {
    setReply(ctx);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReply(null);
  }, []);

  const handleSend = useCallback(
    async (text: string): Promise<boolean> => {
      if (!chat) return false;
      const phone = chat.id.user;
      if (reply) {
        const ok = await onSendReply(phone, reply.messageId, text, chat.isGroup);
        if (ok) setReply(null);
        return ok;
      }
      return onSendMessage(phone, text, chat.isGroup);
    },
    [chat, reply, onSendReply, onSendMessage],
  );

  const handleSendImage = useCallback(
    async (base64: string, filename: string, caption?: string) => {
      if (!chat) return false;
      return onSendImage(chat.id.user, base64, filename, caption, chat.isGroup);
    },
    [chat, onSendImage],
  );

  const handleSendFile = useCallback(
    async (base64: string, filename: string, caption?: string) => {
      if (!chat) return false;
      return onSendFile(chat.id.user, base64, filename, caption, chat.isGroup);
    },
    [chat, onSendFile],
  );

  const handleSendVoice = useCallback(
    async (base64Ptt: string) => {
      if (!chat) return false;
      return onSendVoice(chat.id.user, base64Ptt, chat.isGroup);
    },
    [chat, onSendVoice],
  );

  const handleSendLocation = useCallback(
    async (location: LocationData) => {
      if (!chat) return false;
      return onSendLocation(chat.id.user, location, chat.isGroup);
    },
    [chat, onSendLocation],
  );

  const handleSendLink = useCallback(
    async (url: string, caption?: string) => {
      if (!chat) return false;
      return onSendLink(chat.id.user, url, caption, chat.isGroup);
    },
    [chat, onSendLink],
  );

  const handleReact = useCallback(
    async (messageId: string, reaction: string) => {
      await onReactMessage(messageId, reaction);
    },
    [onReactMessage],
  );

  const handleScrollToMessage = useCallback((targetId: string) => {
    if (!containerRef.current) return;
    const el = document.getElementById(`msg-${targetId}`);
    if (el) {
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const scrollTop = container.scrollTop + elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2;
      container.scrollTo({ top: scrollTop, behavior: "smooth" });
    }
  }, []);

  const { t } = useTranslation("dashboard");

  if (!chat) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-background-100/40 min-h-0 relative">
        {onBack && (
          <div className="absolute top-3 left-3">
            <button
              onClick={onBack}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 cursor-pointer transition-colors"
              aria-label={t("chatWindow.back")}
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
          </div>
        )}
        <div className="text-center px-6 max-w-[400px]">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-200/60 flex items-center justify-center">
            <i className="ri-whatsapp-line text-3xl text-foreground-300"></i>
          </div>
          <h3 className="font-heading text-lg text-foreground-700 mb-2">
            {t("chatWindow.title")}
          </h3>
          <p className="text-foreground-500 text-sm max-w-xs mx-auto leading-relaxed">
            {t("chatWindow.emptyText")}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <ChatWindowShimmer />;
  }

  const name = getChatName(chat);
  const isGroup = chat.isGroup;
  const phone = chat.id.user;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-background-100/30">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2.5 bg-background-50 border-b border-background-200/60">
        {onBack && (
          <button
            onClick={onBack}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-600 cursor-pointer transition-colors flex-shrink-0"
            aria-label={t("chatWindow.back")}
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
        )}
        <div className="flex items-center gap-3 flex-1 min-w-0 max-w-[700px] mx-auto w-full">
          {chat.contact?.profilePicThumbObj?.img ? (
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={chat.contact.profilePicThumbObj.img}
                alt={name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
              style={{
                background: `oklch(0.55 0.15 ${hashColor(chat.id._serialized)})`,
              }}
            >
              {getInitials(name)}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-foreground-950 truncate">
              {name}
            </h3>
            <p className="text-[11px] text-foreground-400 truncate">
              {isGroup ? t("chatWindow.group") : chat.contact?.isBusiness ? t("chatWindow.business") : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onRetryMessages(chat.id._serialized)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-400 cursor-pointer transition-colors"
            title={t("chatWindow.refreshMessages")}
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-3">
        <div className="max-w-[700px] mx-auto">
          {error && (
            <div className="flex items-center justify-center py-10">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                </div>
                <p className="text-foreground-500 text-xs mb-2">{error}</p>
                <button
                  onClick={() => onRetryMessages(chat.id._serialized)}
                  className="text-xs text-secondary-600 hover:text-secondary-700 font-medium cursor-pointer"
                >
                  {t("chatList.tryAgain")}
                </button>
              </div>
            </div>
          )}

          {!error && visibleMessages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-200/60 flex items-center justify-center">
                  <i className="ri-chat-1-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">{t("chatWindow.noMessages")}</p>
                <p className="text-foreground-400 text-xs mt-1">
                  {t("chatWindow.startConversation")}
                </p>
              </div>
            </div>
          )}

          {!error && visibleMessages.length > 0 && (
            <div>
              {visibleMessages.map((msg, idx) => {
                const prevMsg = idx > 0 ? visibleMessages[idx - 1] : null;
                const showDateLabel =
                  !prevMsg ||
                  new Date(prevMsg.timestamp * 1000).toDateString() !==
                    new Date(msg.timestamp * 1000).toDateString();

                return (
                  <div key={msg.id} id={`msg-${msg.id}`} className="transition-all duration-500 rounded-lg origin-center">
                    {showDateLabel && (
                      <div className="flex justify-center mb-2 mt-2">
                        <span className="text-[11px] text-foreground-400 bg-background-200/50 px-3 py-0.5 rounded-full">
                          {formatDateLabel(msg.timestamp || msg.t, t)}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      message={msg}
                      isGroup={isGroup}
                      contactName={name}
                      positionInGroup={messageGroupPositions[idx]}
                      onReply={handleReply}
                      onReact={handleReact}
                      onDownloadMedia={onDownloadMedia}
                      onAiReply={onAiReply}
                      aiReplyLoading={aiReplying}
                      onScrollToMessage={handleScrollToMessage}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply preview */}
      {reply && <ReplyPreview reply={reply} onCancel={handleCancelReply} />}

      {/* Input area */}
      <div className="flex-shrink-0 px-4 py-2.5 bg-background-50">
        <div className="max-w-[700px] mx-auto w-full">
          <ChatInput
            onSend={handleSend}
            onSendImage={handleSendImage}
            onSendFile={handleSendFile}
            onSendVoice={handleSendVoice}
            onSendLocation={handleSendLocation}
            onSendLink={handleSendLink}
            sending={sendingMessage}
            aiReplying={aiReplying}
          />
        </div>
      </div>
    </div>
  );
}

function hashColor(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}